/**
 * PostHog — product analytics on our own box.
 *
 * WHY THIS EXISTS BESIDE lib/analytics.ts. The two answer different questions
 * under different rules. Google Analytics gets the ROUTE and pseudonymous ids,
 * because Google's terms prohibit personal data and GA4 collapses a
 * high-cardinality path into an `(other)` bucket that answers nothing. PostHog
 * runs on insights.safirios.com, a droplet we own, so it gets the real url and
 * the real person -- which is what self-hosting was for.
 *
 * WHY THIS POSTS DIRECTLY INSTEAD OF USING posthog-js. The SDK does not work
 * here, in either form. Bundled from npm and loaded from the instance's own
 * /static/array.js it behaves identically: remote config fetched, flags
 * fetched, surveys loaded, persistence written, `__loaded` true, `capture` a
 * function, correct api_host -- and every capture() silently dropped. Its
 * internal request queue is never built (`__request_queue` exists, the real one
 * does not), and capture() opens with a guard on exactly that, so events vanish
 * with no request, no error and no debug line even with ph_debug on.
 *
 * Ruled out before giving up on it: our own code (a direct capture() from the
 * browser console does nothing either), bot detection (headed Chromium,
 * userAgentData ["Chromium","Not=A?Brand"]), opt-out, Do Not Track, and CORS --
 * a plain cross-origin fetch from this very origin to /i/v0/e/ returns
 * {"status":"Ok"} and lands in ClickHouse.
 *
 * So the transport is fine and only the SDK is broken. This sends the same
 * documented payload the SDK would, and is small enough to read in one sitting.
 *
 * WHAT THIS GIVES UP: autocapture, feature flags, session replay and surveys,
 * all of which are SDK features. Pageviews, custom events, identity and person
 * properties -- the reasons this was wanted -- all work.
 *
 * ONE PROJECT, SEVERAL PRODUCTS. The free self-hosted licence allows exactly one
 * organisation and one project, so every event carries `product` and Riley,
 * SafiriOS, SafiriTrack and Posta are separated by filtering on it.
 */

import type { Router } from 'vue-router';

const KEY = ((import.meta.env['VITE_POSTHOG_KEY'] as string | undefined) ?? '').trim();
const HOST = (((import.meta.env['VITE_POSTHOG_HOST'] as string | undefined) ?? 'https://insights.safirios.com').trim()).replace(/\/$/, '');

/** Which of the estate's surfaces this bundle is. Every event carries it. */
const PRODUCT = 'riley-customer-portal';

/** Where the SDK posts too; taken from the instance's own remote config. */
const ENDPOINT = '/i/v0/e/';
const ANON_KEY = 'riley_customer_ph_anon_id';

const enabled = KEY !== '' && typeof window !== 'undefined';

/**
 * A stable id for this browser until somebody signs in.
 *
 * Kept so a visitor's first look and the account they sign into later join up
 * as one person rather than two. Falls back to a per-load id when storage is
 * unavailable (private windows, blocked cookies) rather than throwing.
 */
function anonId(): string {
  try {
    const existing = localStorage.getItem(ANON_KEY);
    if (existing) return existing;
    const made = crypto.randomUUID();
    localStorage.setItem(ANON_KEY, made);
    return made;
  } catch {
    return 'anon-unstored';
  }
}

let currentId: string | null = null;

function send(event: string, properties: Record<string, unknown>): void {
  if (!enabled) return;
  const body = JSON.stringify({
    api_key: KEY,
    event,
    distinct_id: currentId ?? anonId(),
    timestamp: new Date().toISOString(),
    properties: {
      product: PRODUCT,
      $current_url: window.location.href,
      $pathname: window.location.pathname,
      $host: window.location.host,
      $referrer: document.referrer || undefined,
      ...properties,
    },
  });
  try {
    // keepalive so a pageview fired as somebody navigates away still leaves.
    void fetch(`${HOST}${ENDPOINT}`, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },   // a "simple" request: no CORS preflight
      body,
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // Analytics must never break the page it is measuring.
  }
}

export function initProduct(): void {
  if (!enabled) return;
  currentId = anonId();
}

/** Who this is. Unlike the GA module, the real person: this is our own box. */
export function identifyProduct(user: { id: string | number; email?: string; name?: string; role?: string; accountId?: string | number } | null): void {
  if (!enabled) return;

  if (!user) {
    // Only meaningful once somebody was actually signed in; going back to the
    // anonymous id on every load would break the first-visit-to-account link.
    if (currentId && currentId !== anonId()) {
      currentId = anonId();
    }

    return;
  }

  const previous = currentId;
  currentId = String(user.id);

  // $identify joins the anonymous history to the account. $set writes the
  // person's properties, which on our own box may be the real ones.
  send('$identify', {
    $anon_distinct_id: previous ?? anonId(),
    $set: { email: user.email, name: user.name, role: user.role, account_id: user.accountId, product: PRODUCT },
  });
}

/** One pageview, with the real path. */
export function captureProductPageview(pathname: string): void {
  send('$pageview', { path: pathname });
}

/** Anything else worth recording. */
export function captureProductEvent(event: string, properties: Record<string, unknown> = {}): void {
  send(event, properties);
}

/**
 * Send one pageview per navigation. Vue Router replaces the document without a
 * browser load, so without this only the first screen would be counted.
 */
export function installProduct(router: Router): void {
  initProduct();
  router.afterEach((to) => {
    captureProductPageview(to.fullPath);
  });
}
